import { useId } from "react";
import { cn } from "../utils/cn";

// ── Props ─────────────────────────────────────────────────────────────────────

export interface SwitchProps {
  /** Whether the switch is on */
  checked: boolean;
  /** Called with the new state when toggled */
  onChange: (checked: boolean) => void;
  /** Label rendered to the right of the track */
  label?: string;
  /** Smaller description text below the label */
  description?: string;
  /** Controls track size and thumb diameter */
  size?: "sm" | "md" | "lg";
  /** Prevents interaction */
  disabled?: boolean;
  /** Additional className on the root element */
  className?: string;
}

// ── Size tokens ───────────────────────────────────────────────────────────────
//
// Each size: track dimensions, thumb dimensions, and the translate-x distance
// when checked. Formula: track-width − thumb-size − 2 × inset-padding.
//
// sm:  track 32×16px, thumb 12×12px, inset 2px → travel = 32−12−4 = 16px
// md:  track 44×24px, thumb 20×20px, inset 2px → travel = 44−20−4 = 20px
// lg:  track 56×32px, thumb 24×24px, inset 4px → travel = 56−24−8 = 24px

const SIZE = {
  sm: {
    track:      "w-8 h-4",
    thumb:      "w-3 h-3",
    thumbInset: "top-0.5 left-0.5",
    thumbOn:    "translate-x-4",
  },
  md: {
    track:      "w-11 h-6",
    thumb:      "w-5 h-5",
    thumbInset: "top-0.5 left-0.5",
    thumbOn:    "translate-x-5",
  },
  lg: {
    track:      "w-14 h-8",
    thumb:      "w-6 h-6",
    thumbInset: "top-1 left-1",
    thumbOn:    "translate-x-6",
  },
} as const;

// ── Component ─────────────────────────────────────────────────────────────────

export function Switch({
  checked,
  onChange,
  label,
  description,
  size = "md",
  disabled = false,
  className,
}: SwitchProps) {
  const id = useId();
  const s = SIZE[size];

  return (
    <div
      className={cn(
        "inline-flex items-start gap-3",
        disabled && "opacity-60 cursor-not-allowed",
        className,
      )}
    >
      {/* Track + thumb */}
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          "relative shrink-0 rounded-full transition-colors duration-200",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-purple",
          "focus-visible:ring-offset-2 focus-visible:ring-offset-surface",
          "disabled:cursor-not-allowed",
          s.track,
          checked
            ? "bg-brand-purple"
            : "bg-zinc-300 dark:bg-zinc-500",
        )}
      >
        {/* Thumb */}
        <span
          aria-hidden="true"
          className={cn(
            "absolute rounded-full bg-white shadow-sm",
            "transition-transform duration-200 ease-in-out",
            s.thumb,
            s.thumbInset,
            checked ? s.thumbOn : "translate-x-0",
          )}
        />
      </button>

      {/* Label + description */}
      {(label || description) && (
        <label
          htmlFor={id}
          className={cn(
            "flex flex-col gap-0.5 leading-tight",
            !disabled && "cursor-pointer",
          )}
        >
          {label && (
            <span className="text-base font-medium text-content-primary">
              {label}
            </span>
          )}
          {description && (
            <span className="text-sm text-content-secondary">
              {description}
            </span>
          )}
        </label>
      )}
    </div>
  );
}

Switch.displayName = "Switch";
